import { createSliceFoundation, CrudEditorPageRenderHeaderParams, EditMode, EntityEditorFormSimple, EntityEditorPage, getBaseImpures, getBaseReducers, PropsFrom, SliceEntityEditorPage, sliceEntityEditorPageOnlyForExtension, StateFrom } from "@crispico/foundation-react";
import { Wizard, WizardRRC, WizardStep } from "@crispico/foundation-react/components/Wizard/Wizard";
import { EntityDescriptor, FieldDescriptor } from "@crispico/foundation-react/entity_crud/EntityDescriptor";
import { EntityFilterMode } from "@crispico/foundation-react/entity_crud/fieldEditors/FilterFieldEditor";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import React, { ReactNode } from "react";
import { Divider, Header, Menu, Message, Icon, SemanticICONS, Container, Segment } from "semantic-ui-react";
import { BarsDistanceTimeInTerritoriesTabRRC } from "./DistanceTimeInTerritories/BarsDistanceTimeInTerritoriesTab/BarsDistanceTimeInTerritoriesTab";
import { PieDistanceTimeInTerritoriesTabRRC } from "./DistanceTimeInTerritories/PieDistanceTimeInTerritoriesTab/PieDistanceTimeInTerritoriesTab";
import { equipmentUsageConfigDescriptor, equipmentUsageDayConfigDescriptor, EquipmentUsageTabRRC} from "./equipmentUsageTab/EquipmentUsageTab";
import { fieldHistoryConfigDescriptor, fieldsHistoryConfigDescriptor, FieldsHistoryTabRRC } from "./FieldsHistory/FieldsHistory";
import { HistogramCountInTerritoriesTabRRC } from "./HistogramCountInTerritoriesTab/HistogramCountInTerritoriesTab";
import { historyCompareConfigDescriptor, HistoryCompareTab, sliceHistoryCompareTab } from "./HistoryCompare/HistoryCompareTab";
import { HumanResourcePeriodsRRC } from "./HumanResourcePeriods/HumanResourcePeriods";
import { HistoryTrackReportRRC, historyTrackReportDescriptor } from "./HistoryTrack/HistoryTrackReport";
import { CalculateEquipmentResourceUsageBasedOnNbMissionsPageRRC, equipmentResourceUsageBasedOnNbMissionsConfigDescriptor } from "./CalculateEquipmentResourceUsageBasedOnNbMissionsPage/CalculateEquipmentResourceUsageBasedOnNbMissionsPage";

function getChartTypeMessage(type: string | undefined) {
    return type ? _msg("Chart." + type + ".title") : "";
}

export class ChartEntityDescriptor extends EntityDescriptor {
    constructor() {
        super({
            name: "Chart",
            miniFields: ["name", "type"],
            icon: "chart pie",
            defaultSort: { field: 'name', direction: 'DESC' },
            toMiniString: function toMiniString(entityWithMiniFields: any): string {
                return entityWithMiniFields.name + " - " + getChartTypeMessage(entityWithMiniFields.type);
            }
        });
    }

    protected customize() {
        this.isInDefaultColumnConfig(false, "config", "savedData", "automaticGenerationRecipientList", "automaticGenerationCron") // CS: disabled quickly, because they induced perf issues in the table
            .addFieldDescriptor({ name: "id", type: FieldType.number, enabled: false, isInDefaultColumnConfig: false })
            .addFieldDescriptor({ name: "name", type: FieldType.string })
            .addFieldDescriptor({ name: "type", type: FieldType.string, isInDefaultColumnConfigForEditor: false }, new class extends FieldDescriptor {
                renderField(entity: any) {
                    return getChartTypeMessage(entity.type);
                }
            })
            .addFieldDescriptor({ name: "percentage", type: FieldType.progress })
            .addFieldDescriptor({ name: "automaticGenerationRecipientList", type: "RecipientList" })
            .addFieldDescriptor({ name: "automaticGenerationCron", type: FieldType.cron, optional: true })
            .addFieldDescriptor({ name: "lastAutomaticGenerationDate", type: FieldType.date })
            .addFieldDescriptor({ name: "icon", type: FieldType.string })
            .addFieldDescriptor({ name: "color", type: FieldType.color })
            .addFieldDescriptor({ name: "showInMenu", type: FieldType.boolean })
            .addFieldDescriptor({ name: "positionInMenu", type: FieldType.number })
            .addFieldDescriptor({ name: "organization", type: "Organization"})
        ;

        this.infoEditor.slice = sliceEntityEditorPageChart.setEntityDescriptor(this);
        this.infoEditor.wrappedComponentClass = ChartEntityEditorPage;
        this.infoEditor.routeProps!.routeIsModal = false;
    }
}

const CORRUPT = "corrupt";

export const sliceEntityEditorPageChart = createSliceFoundation(class Ext extends SliceEntityEditorPage {
    initialState = {
        ...sliceEntityEditorPageOnlyForExtension.initialState,
        config: undefined as any | typeof CORRUPT
    }

    nestedSlices = {
        ...sliceEntityEditorPageOnlyForExtension.nestedSlices,
        historyCompareTab: sliceHistoryCompareTab,
    }

    reducers = {
        ...sliceEntityEditorPageOnlyForExtension.reducers, ...getBaseReducers<Ext>(this),

        updateChartType(state: StateFrom<Ext>, type: string) {
            state.entity.type = type;
        },

        clearStateBeforeAddOrEditSuper: sliceEntityEditorPageOnlyForExtension.reducers.clearStateBeforeAddOrEdit,
        clearStateBeforeAddOrEdit(state: StateFrom<Ext>) {
            this.clearStateBeforeAddOrEditSuper(state);
            state.config = {};
        },

        onModeLoadedSuper: sliceEntityEditorPageOnlyForExtension.reducers.onModeLoaded,
        onModeLoaded(state: StateFrom<Ext>, entity: any) {
            this.onModeLoadedSuper(state, entity);
            try {
                state.config = JSON.parse(entity.config);
            } catch (e) {
                state.config = CORRUPT;
            }
        },

        commitAutomaticGeneration(state: StateFrom<Ext>, values: any) {
            if (values) {
                state.entity.automaticGenerationRecipientList = values.automaticGenerationRecipientList;
                state.entity.automaticGenerationCron = values.automaticGenerationCron;
            }
        },
    }

    impures = {
        ...sliceEntityEditorPageOnlyForExtension.impures, ...getBaseImpures<Ext>(this),

        saveSuper: sliceEntityEditorPageOnlyForExtension.impures.save,
        async save(entity: any) {
            const additionalConfigParamsAsString = this.getState().config.additionalConfigParamsAsString;
            const config = { ...this.getState().config, ...(additionalConfigParamsAsString ? JSON.parse(additionalConfigParamsAsString) : {}) }
            entity = { ...entity, config: JSON.stringify(config) };
            delete entity.savedData;
            return this.saveSuper(entity, undefined, undefined, {
                initialFieldsAndValues: {
                    config: entity.config
                }
            });
        }
    }

    getFieldsToRequest() {
        return super.getFieldsToRequest().concat(["config", "savedData"]);
    }
});

export type ChartDescriptor = {
    typeKeySuffix: string,
    icon: SemanticICONS,
    tab: any,
    descriptor: EntityDescriptor,
}

const chartConfigEntityDescriptor = (name: string) => {
    return new EntityDescriptor({name: name}, false)
        .addFieldDescriptor({ name: "startDate", type: FieldType.date })
        .addFieldDescriptor({ name: "endDate", type: FieldType.date })
        .addFieldDescriptor({ name: "equipmentFilter", type: FieldType.filter, mode: EntityFilterMode.OBJECT, entityName: "EquipmentResource" })
        .addFieldDescriptor({ name: "territories", type: FieldType.oneToMany('Territory') })
}

export const chartTypes = () : { [type: string]: ChartDescriptor } => { 
    return {
    tripSummary: { typeKeySuffix: "table", icon: "table", tab: HistoryTrackReportRRC, descriptor: historyTrackReportDescriptor },
    tripPerformance: { typeKeySuffix: "table", icon: "table", tab: HistoryTrackReportRRC, descriptor: historyTrackReportDescriptor },
    fieldsHistory: { typeKeySuffix: "table", icon: "table", tab: FieldsHistoryTabRRC, descriptor: fieldsHistoryConfigDescriptor },
    fieldHistory: { typeKeySuffix: "table", icon: "table", tab: FieldsHistoryTabRRC, descriptor: fieldHistoryConfigDescriptor },
    equipmentResourceUsageBasedOnNbMissions: { typeKeySuffix: "table", icon: "table", tab: CalculateEquipmentResourceUsageBasedOnNbMissionsPageRRC, descriptor: equipmentResourceUsageBasedOnNbMissionsConfigDescriptor() },
    equipmentUsage: { typeKeySuffix: "bars", icon: "chart bar", tab: EquipmentUsageTabRRC, descriptor: equipmentUsageConfigDescriptor },
    equipmentUsageDay: { typeKeySuffix: "lines", icon: "chart line", tab: EquipmentUsageTabRRC, descriptor: equipmentUsageDayConfigDescriptor },
    histogramCountInTerritories: {
        typeKeySuffix: "histogram", icon: "chart line", tab: HistogramCountInTerritoriesTabRRC, descriptor: chartConfigEntityDescriptor("HistogramCountInTerritoriesTab").addFieldDescriptor({ name: "samplingStep", type: FieldType.number })
    },
    pieDistanceTimeInTerritories: {
        typeKeySuffix: "pie", icon: "chart pie", tab: PieDistanceTimeInTerritoriesTabRRC, descriptor: chartConfigEntityDescriptor("PieDistanceTimeInTerritoriesTab")
    },
    barsDistanceTimeInTerritories: {
        typeKeySuffix: "bars", icon: "chart bar", tab: BarsDistanceTimeInTerritoriesTabRRC, descriptor: chartConfigEntityDescriptor("BarsDistanceTimeInTerritoriesTab").addFieldDescriptor({ name: "samplingStep", type: FieldType.number })
    },
    historyCompare: { typeKeySuffix: "lines_bars", icon: "history", tab: HistoryCompareTab, descriptor: historyCompareConfigDescriptor },
    humanResourcePeriods: { typeKeySuffix: "hr_periods", icon: "line graph", tab: HumanResourcePeriodsRRC, descriptor: new EntityDescriptor({name: "HumanResourcePeriods"}, false).addFieldDescriptor({ name: "additionalConfigParamsAsString", type: FieldType.string  }) }
    }
}

type ChartType = 'histogramCountInTerritories' | 'pieDistanceTimeInTerritories' | 'barsDistanceTimeInTerritories' | "equipmentUsage" | "fieldsHistory" | "fieldHistory" | "historyCompare" | "tripSummary" | "tripPerformance" | "equipmentResourceUsageBasedOnNbMissions";

export function getChartComponentClass(type: string, throwErrorIfNotFound: boolean = true): any {
    const ComponentClass = chartTypes()[type]?.tab;
    if (!ComponentClass && throwErrorIfNotFound) { throw new Error("Unknown chart type: " + type + "; known types = " + Object.keys(chartTypes())); }
    return ComponentClass;
}

export class ChartEntityEditorPage extends EntityEditorPage<PropsFrom<typeof sliceEntityEditorPageChart>> {

    protected refForm2 = React.createRef<EntityEditorFormSimple>();
    protected refWizard = React.createRef<Wizard>();
    protected automaticGenerationTabRef = React.createRef<any>();

    protected onMatchChanged(match: any) {
        this.refWizard.current?.props.r.reset();
        super.onMatchChanged(match);
    }

    protected getExtraTabPanes() {
        if (this.props.mode === EditMode.ADD) {
            return super.getExtraTabPanes();
        }

        return [{
                routeProps: { path: "/chart" },
                menuItemProps: { icon: "chart pie", content: "Chart" },
                render: () => {
                    const ComponentClass = getChartComponentClass(this.props.entity?.type, false);
                    const chartType: ChartType = this.props.entity?.type;
                    return this.renderErrorIfNeeded(() => {
                        if (chartType == "historyCompare") {
                            return <HistoryCompareTab  dispatchers={this.props.dispatchers.historyCompareTab} {...this.props.historyCompareTab} config={this.props.config} entity={this.props.entity} editor={this}/>
                        }
                        return <ComponentClass id={chartType} entity={this.props.entity} config={this.props.config} editorDispatchers={this.props.dispatchers} />
                    })
                }
            }, ...super.getExtraTabPanes(), {
                routeProps: { path: "/automaticGeneration" }, menuItemProps: { icon: "clock outline", content: _msg("AutomaticGeneration.label") },
    
                commit: () => {
                    if (this.automaticGenerationTabRef.current) {
                        this.props.dispatchers.commitAutomaticGeneration(this.automaticGenerationTabRef.current.formikContext.values)
                    }
                },
    
                render: () => {
                    const automaticGenerationEntityDescriptor = new EntityDescriptor({name: "AutomaticGeneration"})
                        .addFieldDescriptor({ name: "automaticGenerationRecipientList", type: "RecipientList" })
                        .addFieldDescriptor({ name: "automaticGenerationCron", type: FieldType.cron })
                    const cls = this.getContainerCssClasses();
                    return <Container className={cls.outer} fluid>
                        <Segment className={cls.inner}>
                            {this.renderHeader({})}
                            <EntityEditorFormSimple entityDescriptor={automaticGenerationEntityDescriptor} entity={this.props.entity} 
                                ref={this.automaticGenerationTabRef} hideButtonBar />
                        </Segment>
                    </Container>
                }
            }];
    }

    renderHeader(params: CrudEditorPageRenderHeaderParams) {
        if (this.props.mode === EditMode.ADD) {
            return null;
        }
        return super.renderHeader(params);
    }

    protected renderFormNew() {
        const chartDescriptor = chartTypes()[this.props.entity.type];
        return (<>
            <Menu fluid vertical>
                <Row type={this.props.entity.type} />
            </Menu>
            <Divider />
            {super.renderForm()}
            <Divider />
            {this.renderErrorIfNeeded(() => <EntityEditorFormSimple hideButtonBar ref={this.refForm2} entity={this.props.config} entityDescriptor={chartDescriptor.descriptor} />)}
        </>);
    }

    protected renderErrorIfNeeded(what: () => ReactNode) {
        if (this.props.mode === EditMode.EDIT_LOADING) {
            return _msg("general.loading");
        }
        const chartDescriptor = chartTypes()[this.props.entity?.type];
        if (chartDescriptor && this.props.config !== CORRUPT) {
            return what();
        } else {
            return <Message error>{_msg("Chart.noType", this.props.entity?.type)}</Message>
        }
    }

    protected renderForm() {
        if (this.props.mode === EditMode.ADD) {
            const m: any = this.getMainMenuItemProps();
            const steps: WizardStep[] = [
                {
                    title: _msg("Chart.chooseType"),
                    icon: "question circle outline",
                    nextDisabled: () => !this.props.entity.type,
                    render: () => (
                        <Menu fluid vertical>
                            {Object.keys(chartTypes()).map(type => <Row parentProps={this.props} type={type} />)}
                        </Menu>
                    ),
                },
                { title: m.content, icon: m.icon, render: () => this.renderFormNew() }
            ];
            return <WizardRRC id="wizardChart" steps={steps}
                onFinishClick={() => this.onSave()} onCancelClick={this.onCancel} />
        } else {
            return this.renderFormNew();
        }
    }

    protected getContainerCssClasses() {
        const result = super.getContainerCssClasses();
        if (this.refWizard.current?.props.s.currentStepIndex === 0) {
            result.inner += " ChartEditorPage_innerFormContainer";
        }

        return result;
    }

    protected commitMain() {
        super.commitMain();
        if (!this.refForm2.current) {
            return;
        }
        this.props.dispatchers.setInReduxState({ config: this.refForm2.current.formikContext.values });
    }

    protected isDirty() {
        return super.isDirty() && this.refForm2.current?.formikContext.dirty;
    }
}

function Row(props: { type: string, parentProps?: PropsFrom<typeof sliceEntityEditorPageChart> }) {
    const cd = chartTypes()[props.type];
    return (<Menu.Item className="flex-container-row" link active={props.type === props.parentProps?.entity.type} onClick={() => props.parentProps?.dispatchers.updateChartType(props.type)}>
        <div>
            <Icon name={cd.icon} size="massive"></Icon>
        </div>
        <div className="flex-grow">
            <Header as="h2" dividing>{getChartTypeMessage(props.type)}<Header.Subheader>{_msg("Chart.type")} <b>{_msg("Chart.type." + cd.typeKeySuffix)}</b></Header.Subheader></Header>
            <p>{_msg("Chart." + props.type + ".content")}</p>
        </div>
    </Menu.Item>)
}
